import { Component, OnInit, TemplateRef } from "@angular/core";
import {
  FormGroup,
  FormControl,
  Validators,
  FormArray,
  FormBuilder,
} from "@angular/forms";
import { Router, Params, ActivatedRoute } from "@angular/router";
import { UsersService } from "./../users.service";
import { Users } from "./../users.model";
import { Observable } from "rxjs/Observable";
import { startWith } from "rxjs/operators/startWith";
import { map } from "rxjs/operators/map";
import { sortBy, get, lowerCase, includes, chain } from "lodash";
import { BsModalService } from "ngx-bootstrap";
import { BsModalRef } from "ngx-bootstrap/modal/bs-modal-ref.service";
import { unescape } from "lodash";
import { Encoder } from "../../../utils/encoder";

@Component({
  selector: "app-users",
  templateUrl: "./form.component.html",
  styleUrls: ["./form.component.scss"],
})
export class UsersFormComponent implements OnInit {
  public usersForm: FormGroup;
  campaignList: any[];
  modalRef: BsModalRef;

  showAlert = false;
  errorMessage: string = null;

  users: Users = new Users();
  isEditing: boolean;
  loading: boolean = false;
  descriptionValidate: boolean = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private usersService: UsersService,
    private modalService: BsModalService,
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    this.usersService.hasJustRegistered = false;

    const data = get(this.activatedRoute, ["snapshot", "data"], {});
    this.campaignList = this.getCampagignList().items;
    // @ts-ignore
    this.isEditing = !!data.user;
    if (this.isEditing) {
      // @ts-ignore
      this.users = data.user.data;
    }
    this.createFormControls();
  }

  createFormControls() {
    if (this.isEditing) {
      if (this.users.hasOwnProperty("id")) {
        this.usersForm = this.fb.group({
          id: [this.users.id],
          name: [this.users.name, [Validators.required]],
          phoneNumber: [
            this.users.phoneNumber,
            [Validators.required, this.internationalPhoneNumberValidator],
          ],
          email: [this.users.email, [Validators.required, Validators.email]],
          enabled: [this.users.enabled, [Validators.required]],
          campaignIdsObjArr: this.fb.array([]),
        });
        this.users.campaignList.forEach((campaign) => {
          const curCampaign = this.campaignList.find(
            ({ campaignId }) => campaignId === campaign.campaignId
          );
          const newCampaign: FormGroup = this.fb.group({
            randowId: [campaign.campaignId, [Validators.required]],
            campaignId: [campaign.campaignId, [Validators.required]],
            campaignName: [curCampaign.campaignName, [Validators.required]],
            country: [curCampaign.campaignMainCountry],
            campaignStartDate: [
              curCampaign.campaignStartDt
                ? this.getLocalDate(curCampaign.campaignStartDt * 1000)
                : null,
            ],
            campaignEndDate: [
              curCampaign.campaignEndDt
                ? this.getLocalDate(curCampaign.campaignEndDt * 1000)
                : null,
            ],
          });
          this.campaignIdsObjArr.push(newCampaign);
        });
        this.usersForm.get("id").disable();
      } else {
        this.router.navigate(["/users"]);
      }
    } else {
      this.usersForm = this.fb.group({
        id: [null],
        name: [null, [Validators.required]],
        phoneNumber: [
          null,
          [Validators.required, this.internationalPhoneNumberValidator],
        ],
        email: [null, [Validators.required, Validators.email]],
        enabled: [true, [Validators.required]],
        campaignIdsObjArr: this.fb.array([]),
      });
    }
  }

  private getCampagignList(): any {
    const filterBefore = get(this.activatedRoute, ["snapshot", "data", "campaigns"], []);
    return {...filterBefore, items: filterBefore.items.filter(item => item.onlyDash === 'true')}
  }

  errorMessageClose(): void {
    this.showAlert = false;
    this.errorMessage = "";
  }

  private dirtyForm(form: FormGroup | FormArray) {
    if (form instanceof FormGroup) {
      Object.keys(form.controls).forEach((key) => {
        const control = form.controls[key];

        if (control instanceof FormGroup || control instanceof FormArray) {
          return this.dirtyForm(control);
        }

        control.markAsDirty();
      });
    }
    if (form instanceof FormArray) {
      form.controls.forEach((c) => {
        return this.dirtyForm(c as FormGroup);
      });
    }
  }

  checkInputValue(e: any) {
    return this.usersForm.get(e.target.id).setValue(e.target.value);
  }

  get campaignIdsObjArr(): FormArray {
    return this.usersForm.get("campaignIdsObjArr") as FormArray;
  }

  addCampaign() {
    const newCampaign: FormGroup = this.fb.group({
      randowId: [Date.now(), [Validators.required]],
      campaignId: [null, [Validators.required]],
      campaignName: [null, [Validators.required]],
      country: [null],
      campaignStartDate: [null],
      campaignEndDate: [null],
    });
    this.campaignIdsObjArr.push(newCampaign);
  }

  usersSubmit(template: TemplateRef<any>): any {
    this.dirtyForm(this.usersForm);
    const users: any = {
      name: this.usersForm.get("name").value,
      email: this.usersForm.get("email").value,
      phoneNumber: this.usersForm.get("phoneNumber").value,
      enabled: this.usersForm.get("enabled").value,
    };
    if (this.campaignIdsObjArr.value.length > 0) {
      users.campaignIds = this.campaignIdsObjArr.value.map(
        ({ campaignId }) => campaignId
      );
    }
    if (this.usersForm.valid) {
      this.loading = true;
      if (!this.isEditing) {
        this.usersService.saveUsers(users).subscribe(
          (res) => {
            this.loading = false;
            this.modalRef = this.modalService.show(template, {
              ignoreBackdropClick: true,
            });
          },
          (error) => {
            this.loading = false;
            this.showAlert = true;
            this.errorMessage = error;
          }
        );
      } else {
        this.activatedRoute.params.subscribe((value) => {
          users.userId = value.userId;
          users.id = value.userId;
        });
        this.usersService.updateUsers(users).subscribe(
          (res) => {
            this.loading = false;
            this.router.navigate(["/users"]);
          },
          (error) => {
            this.loading = false;
            this.showAlert = true;
            this.errorMessage = error;
          }
        );
      }
    }
  }

  deleteCampaign(i) {
    this.campaignIdsObjArr.removeAt(i);
  }
  verifiData(data, i) {
    const formArray = this.usersForm.get("campaignIdsObjArr") as FormArray;
    const formGroup = formArray.at(i) as FormGroup;
    const campaignId = formGroup.get("campaignId") as FormControl;
    const country = formGroup.get("country") as FormControl;
    const campaignStartDate = formGroup.get("campaignStartDate") as FormControl;
    const campaignEndDate = formGroup.get("campaignEndDate") as FormControl;
    const {
      campaignName: { value },
    } = data.controls;
    if (value) {
      this.campaignList = this.getCampagignList().items.filter(
        ({ campaignName }) =>
          campaignName.toLowerCase().includes(value.toLowerCase())
      );
      const curRealCampagign = this.getCampagignList().items.find(
        ({ campaignName }) => campaignName.toLowerCase() === value.toLowerCase()
      );
      if (curRealCampagign) {
        campaignId.setValue(curRealCampagign.campaignId);
        country.setValue(curRealCampagign.campaignMainCountry);
        campaignStartDate.setValue(
          curRealCampagign.campaignStartDt
            ? this.getLocalDate(curRealCampagign.campaignStartDt * 1000)
            : null
        );
        campaignEndDate.setValue(
          curRealCampagign.campaignEndDt
            ? this.getLocalDate(curRealCampagign.campaignEndDt * 1000)
            : null
        );
      } else {
        campaignId.setValue(null);
        country.setValue(null);
        campaignStartDate.setValue(null);
        campaignEndDate.setValue(null);
      }
    } else {
      this.campaignList = this.getCampagignList().items;
      campaignId.setValue(null);
      country.setValue(null);
      campaignStartDate.setValue(null);
      campaignEndDate.setValue(null);
    }
  }
  focus(data) {
    const {
      campaignName: { value },
    } = data.controls;
    if (value) {
      this.campaignList = this.getCampagignList().items.filter(
        ({ campaignName }) =>
          campaignName.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      this.campaignList = this.getCampagignList().items;
    }
  }

  redirectTo(uri: string) {
    this.router
      .navigateByUrl("/", { skipLocationChange: true })
      .then(() => this.router.navigate([uri]));
  }

  getLocalDate(time) {
    let date = new Date(time);
    return date.toLocaleDateString();
  }

  internationalPhoneNumberValidator(
    control: FormControl
  ): { [key: string]: any } | null {
    const phoneNumber = control.value;

    const pattern = /^\+\d{1,3}\d{6,}$/;

    if (phoneNumber && !pattern.test(phoneNumber)) {
      return { invalidPhoneNumber: true };
    }

    return null;
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/users/form/form.component.ts